import {
  BillingInterval,
  PriceResponse,
} from 'services/api/responses/responseTypes';
import type { IPrice } from './iPrice';

const setDecimals = (value: number): string => {
  const decimalValue = Math.ceil(value).toString().slice(-2);
  return decimalValue === '0' ? '00' : decimalValue;
};

export default class Price implements IPrice {
  id: string;

  currency: string;

  unitAmount: number;

  recurring: {
    interval: BillingInterval;
    intervalCount: number;
  };

  constructor(data: PriceResponse) {
    this.id = data.id;
    this.currency = data.currency;
    this.unitAmount = data.unit_amount;
    this.recurring = {
      interval: data.recurring.interval,
      intervalCount: data.recurring.interval_count,
    };
  }

  // return integer part of price i.e. 12345 -> 123
  intValue(): string {
    const { unitAmount } = this;
    const price: string = unitAmount.toString();
    // if price e.g 45 then should return 0
    return price.length <= 2 ? '0' : price.slice(0, -2);
  }

  // return decimal part of price i.e. 12345 -> 45
  decValue(): string {
    const { unitAmount } = this;
    return unitAmount.toString().slice(-2);
  }

  // return integer part of price i.e. 12345 -> 123 for yearly interval, if monthly then multiplied by 12
  yearlyIntValue(): string {
    const { unitAmount, recurring } = this;
    const { interval } = recurring;
    const yearlyAmount = Math.ceil(
      interval === 'month' ? unitAmount * 12 : unitAmount
    );
    const price: string = yearlyAmount.toString();
    // if price e.g 45 then should return 0
    return price.length <= 2 ? '0' : price.slice(0, -2);
  }

  // return the integer part of price i.e. 12345 -> 123 per month if annually payment is chosen
  yearlyIntValuePerMonth(): string {
    const monthlyAmount = Number(this.yearlyIntValue()) / 12;
    return Math.floor(monthlyAmount).toString();
  }

  // return decimal part of price i.e. 12345 -> 45 for yearly interval, if monthly then multiplied by 12
  yearlyDecValue(): string {
    const { unitAmount, recurring } = this;
    const { interval } = recurring;
    const yearlyAmount = interval === 'month' ? unitAmount * 12 : unitAmount;
    return setDecimals(yearlyAmount);
  }

  // return decimal part of price i.e. 12345 -> 45 per month if annually payment is chosen
  yearlyDecValuePerMonth(): string {
    const { unitAmount, recurring } = this;
    const { interval } = recurring;
    const yearlyAmount = interval === 'year' ? unitAmount / 12 : unitAmount;

    return setDecimals(yearlyAmount);
  }

  // return integer part of price i.e. 12345 -> 123 for monthly interval, if yearly then divided by 12
  monthlyIntValue(): string {
    const { unitAmount, recurring } = this;
    const { interval } = recurring;
    const monthlyAmount = Math.ceil(
      interval === 'month' ? unitAmount : unitAmount / 12
    );
    const price: string = monthlyAmount.toString();
    // if price e.g 45 then should return 0
    return price.length <= 2 ? '0' : price.slice(0, -2);
  }

  // return decimal part of price i.e. 12345 -> 45 for monthly interval, if yearly then divided by 12
  monthlyDecValue(): string {
    const { unitAmount, recurring } = this;
    const { interval } = recurring;
    const monthlyAmount = interval === 'month' ? unitAmount : unitAmount / 12;
    return setDecimals(monthlyAmount);
  }

  toString(interval: BillingInterval): string {
    if (interval === 'year') {
      return `${this.yearlyIntValue()},${this.yearlyDecValue()}`;
    }

    if (interval === 'month') {
      return `${this.monthlyIntValue()},${this.monthlyDecValue()}`;
    }

    return `${this.intValue()},${this.decValue()}`;
  }

  yearlyDifferenceBetween(price: IPrice): string {
    const difference: number = (this.unitAmount - price.unitAmount) / 100;
    return (Math.round(difference * 100) / 100).toFixed(2);
  }
}
